import cloneDeep from 'lodash/cloneDeep'
import IconBarcodeScanFrame from '../components/svg/custom-field/IconBarcodeScanFrame.vue'
import IconCalendar from '../components/svg/custom-field/IconCalendar.vue'
import IconCheckbox from '../components/svg/custom-field/IconCheckbox.vue'
import IconEmail from '../components/svg/custom-field/IconEmail.vue'
import IconFilePreview from '../components/svg/custom-field/IconFile.vue'
import IconGhgEmission from '../components/svg/custom-field/IconGhgEmission.vue'
import IconImage from '../components/svg/custom-field/IconImage.vue'
import IconLocation from '../components/svg/custom-field/IconLocation.vue'
import IconNumber from '../components/svg/custom-field/IconNumber.vue'
import IconPhoneNumber from '../components/svg/custom-field/IconPhoneNumber.vue'
import IconRadio from '../components/svg/custom-field/IconRadio.vue'
import IconReference from '../components/svg/custom-field/IconReference.vue'
import IconSignature from '../components/svg/custom-field/IconSignature.vue'
import IconTextarea from '../components/svg/custom-field/IconTextarea.vue'
import IconTextbox from '../components/svg/custom-field/IconTextbox.vue'
import IconWeightMeasurement from '../components/svg/custom-field/IconWeightMeasurement.vue'

import { ILocationPermission } from '@/views/LocationDetail.vue'
import {
  IAddress,
  IBarcodeDefinitionType,
  IFieldRestriction,
  INotificationRoute,
  IProjectCustomField,
  IRemarkRoute,
  IRepeatedVisitingTrackPointNotificationDefinition,
  IRestrictedTrackPointRoutePath,
} from './types/model/fieldDefinition'
import {
  IBarcodeReportTemplate,
  ICameraFeatures,
  ICarrierUserGroup,
  IClientVisibilities,
  ICountryPhoneInfo,
  ICustomNotificationEvent,
  ICustomValidator,
  ICustomValidatorComparison,
  ICustomValidatorCondition,
  ICustomValidatorSuccess,
  IExternalWebAPIIntegration,
  IProduct,
  ISmaPriTemplateConfig,
  ITrackingPermissions,
  Supplier,
  UploadFile,
} from './types'
import {
  AVAILABLE_DELIMITERS,
  ECustomFieldType,
  ECustomFieldUnitPosition,
  EDisplaySetting,
  EFileKind,
  ELanguageLocale,
  ELinkedBarcodeHrefType,
  EOCRReaderType,
  EReaderType,
  EUserType,
} from './enums'

export const allowExitRoute = ['/login']

export const COLOR_WHITE = Object.freeze('#FFFFFF')

export const COLOR_BLACK = Object.freeze('#000000')

export const BARCODE_TYPE_WHITE_TEXT_PALLET = Object.freeze([
  '#9575CD',
  '#3F51B5',
  '#2196F3',
  '#009688',
  '#865A2D',
  '#979797',
  '#000000',
])

export const BARCODE_TYPE_COLOR_PALLET = Object.freeze([
  '#E57373',
  '#F06292',
  '#BA68C8',
  '#9575CD',
  '#3F51B5',
  '#2196F3',
  '#64B5F6',
  '#4DD0E1',
  '#009688',
  '#4CAF50',
  '#8BC34A',
  '#CDDC39',
  '#FFEB3B',
  '#FFC107',
  '#F57C00',
  '#865A2D',
  '#979797',
  '#000000',
])

export const EXPORT_PDF_COLOR_PALLET = Object.freeze(['#FFFFFF', '#FFEB3B', '#FFC107', '#F476A1', '#64B5F6', '#9AD951'])

export const LOCAL_STORAGE_ITEM: Record<string, string> = Object.freeze({
  VISITOR_ID: `${process.env.NODE_ENV}-Lozi-portal-visitorId`,
  LOCALE: 'Lozi-portal-locale',
  LAST_AUTHORIZED_SYSTEM: `${process.env.NODE_ENV}-last_authorized_system`,
  TRADEWALTZ_BINDING_SETTING: `${process.env.NODE_ENV}-tradewaltz_store_binding_setting`,
})

export const LANGUAGE_SET: Record<string, string[]> = Object.freeze({
  language: [ELanguageLocale.JA, ELanguageLocale.EN, ELanguageLocale.ZHCN, ELanguageLocale.ZHTW],
  languageSwitcherLabel: ['日本語', 'English', '简体中文', '繁體中文'],
})

export const OPERATION_TYPE: Record<string, string> = Object.freeze({
  BULK_CREATE: 'bulkCreate',
  FILE_IMPORT: 'fileImportNew',
})

export const DEFAULT_LOCATION = Object.freeze({
  id: '',
  name: '',
  totalProject: 0,
  totalBarcode: 0,
  isActive: true,
  enterpriseId: '',
  locationProjectPermissions: {} as Record<string, ILocationPermission>,
})

export const DEFAULT_ADDRESS: Supplier = Object.freeze({
  companyName: '',
  country: '',
  postalCode: '',
  prefecture: '',
  address1: '',
  address2: '',
  phoneNumber: '',
})

export const DEFAULT_PRODUCT: IProduct = Object.freeze(({
  clientId: '',
  name: '',
  code: '',
  id: '',
  verion: '0',
  volume: 0,
  material: '',
  supplier: { ...DEFAULT_ADDRESS },
  dimension: {
    width: 0.0,
    height: 0.0,
    depth: 0.0,
    weight: 0.0,
  },
  isAcitve: false,
} as unknown) as IProduct)

export const TIMETABLE_DATETIME: Record<string, string> = Object.freeze({
  DATE_PARAM_FORMAT: 'YYYYMMDD',
  DATE_DISPLAY_FORMAT: 'YYYY/MM/DD',
  DATE_RESPONSE_FORMAT: 'YYYY-MM-DD',
  DATE_TIME_FULL_FORMAT: 'YYYY-MM-DD HH:mm:ss',
  DATE_TIME_FORMAT: 'YYYY/MM/DD hh:mm A',
  TIME_WITHOUT_SECOND_FORMAT: 'HH:mm',
  TIME_WITH_SECOND_FORMAT: 'HH:mm:ss',
  TIME_HOUR_MINUTE_FORMAT: 'HHmm',
  DATETIME_WITHOUT_SECOND_FORMAT: 'YYYY/MM/DD HH:mm',
  DATETIME_WITH_SECOND_FORMAT: 'YYYY/MM/DD HH:mm:ss',
  LL_DATE_DISPLAY_FORMAT: 'LL',
  EXPORTING_MANUFACTURE_COMPLETION_DATE: 'YYYY/MM/DD',
})

export const CUSTOM_FIELD_ICONS: Record<string, string> = Object.freeze({
  TEXTBOX: 'text',
  TEXTAREA: 'textarea',
  MULTI_SELECT: 'multiSelect',
  SINGLE_SELECT: 'singleSelect',
  DATE: 'date',
  NUMBER: 'number',
  LOCATION: 'location',
  SIGNATURE: 'eSign',
  FILES: 'files',
  IMAGES: 'images',
  BARCODE_SCAN_FRAME: 'barcodeScanFrame',
  PHONE_NUMBER: 'phoneNumber',
  EMAIL: 'email',
  REFERENCE: 'reference',
  GHG_EMISSION: 'ghgEmission',
  CALCULATION: 'calculation',
  WEIGHT_MEASUREMENT: 'weightMeasurement',
  LIST_STRING: 'listString',
  DATE_TIME_DATA: 'dateTimeData',
})

export const CUSTOM_FIELD_ICON_COMPONENTS: Record<
  string,
  {
    label: string[]
    icon: unknown
  }
> = Object.freeze({
  [CUSTOM_FIELD_ICONS.TEXTBOX]: { label: ['textbox'], icon: IconTextbox },
  [CUSTOM_FIELD_ICONS.TEXTAREA]: { label: ['textarea'], icon: IconTextarea },
  [CUSTOM_FIELD_ICONS.MULTI_SELECT]: { label: ['checkbox'], icon: IconCheckbox },
  [CUSTOM_FIELD_ICONS.SINGLE_SELECT]: { label: ['radio'], icon: IconRadio },
  [CUSTOM_FIELD_ICONS.DATE]: { label: ['calendar'], icon: IconCalendar },
  [CUSTOM_FIELD_ICONS.NUMBER]: { label: ['number'], icon: IconNumber },
  [CUSTOM_FIELD_ICONS.ADJUSTMENT]: { label: ['number'], icon: IconNumber },
  [CUSTOM_FIELD_ICONS.LOCATION]: { label: ['location'], icon: IconLocation },
  [CUSTOM_FIELD_ICONS.SIGNATURE]: { label: ['signature'], icon: IconSignature },
  [CUSTOM_FIELD_ICONS.FILES]: { label: ['file'], icon: IconFilePreview },
  [CUSTOM_FIELD_ICONS.IMAGES]: { label: ['image'], icon: IconImage },
  [CUSTOM_FIELD_ICONS.BARCODE_SCAN_FRAME]: { label: ['barcodeScanFrame'], icon: IconBarcodeScanFrame },
  [CUSTOM_FIELD_ICONS.PHONE_NUMBER]: { label: ['phoneNumber'], icon: IconPhoneNumber },
  [CUSTOM_FIELD_ICONS.EMAIL]: { label: ['email'], icon: IconEmail },
  [CUSTOM_FIELD_ICONS.REFERENCE]: { label: ['reference'], icon: IconReference },
  [CUSTOM_FIELD_ICONS.GHG_EMISSION]: { label: ['ghgEmission'], icon: IconGhgEmission },
  [CUSTOM_FIELD_ICONS.CALCULATION]: { label: ['calculation'], icon: IconGhgEmission } /** FIXME: Change the icon */,
  [CUSTOM_FIELD_ICONS.WEIGHT_MEASUREMENT]: { label: ['weightMeasurement'], icon: IconWeightMeasurement },
  [CUSTOM_FIELD_ICONS.LIST_STRING]: { label: ['listString'], icon: IconTextbox },
  [CUSTOM_FIELD_ICONS.DATE_TIME_DATA]: { label: ['dateTimeData'], icon: IconCalendar },
})

export const RESTRICT_FIELD_DEFAULT_DATA = Object.freeze({
  isMandatory: false,
  isAvailable: true,
})

export const OCR_FIELD_DEFAULT_DATA = Object.freeze({
  ocrImageAutoSaveField: '',
  ocrReaderType: EOCRReaderType.DEFAULT,
  barcodeReaderType: EReaderType.DEFAULT,
})

export const CAMERA_FEATURES_DEFAULT_DATA: ICameraFeatures = Object.freeze({
  bulkCameraScanResultSetting: {
    isEnable: false,
    delimiter: AVAILABLE_DELIMITERS[8],
  },
})

export const OPTION_FIELD_DEFAULT_DATA = Object.freeze({
  label: '',
  default: true,
})

export const COMMON_CUSTOM_FIELD_DEFAULT_DATA = Object.freeze({
  label: '',
  customFieldKey: '',
  order: 0,
  ...RESTRICT_FIELD_DEFAULT_DATA,
})

export const OPTION_FIELD_DISPLAY_DROPDOWNLIST = Object.freeze({
  isDisplayedDropdownList: false,
})

export const ACTIVATION_CUSTOM_FIELD_EXTRA_DATA = Object.freeze({
  copyOnRecycle: false,
  isHidden: false,
})

const numberInitObject = {
  fieldType: ECustomFieldType.NUMBER,
  minValue: 0,
  maxValue: 100,
  precision: 2,
  default: 0,
  unitType: '',
  position: ECustomFieldUnitPosition.UNKNOWN,
  ...OCR_FIELD_DEFAULT_DATA,
  ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
}

export const PROJECT_CUSTOM_FIELDS_DEFAULT: Record<string, IProjectCustomField> = Object.freeze({
  text: {
    fieldType: ECustomFieldType.TEXT,
    minLength: 0,
    maxLength: 10,
    multiLine: false,
    default: '',
    ...OCR_FIELD_DEFAULT_DATA,
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
    cameraFeatures: {
      ...CAMERA_FEATURES_DEFAULT_DATA,
    },
  },
  number: numberInitObject,
  singleSelect: {
    fieldType: ECustomFieldType.SINGLE_SELECT,
    selections: { r1: { ...OPTION_FIELD_DEFAULT_DATA } },
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
    ...OPTION_FIELD_DISPLAY_DROPDOWNLIST,
  },
  multiSelect: {
    fieldType: ECustomFieldType.MULTI_SELECT,
    selections: { c1: { ...OPTION_FIELD_DEFAULT_DATA } },
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  date: {
    fieldType: ECustomFieldType.DATE,
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  dateTimeData: {
    fieldType: ECustomFieldType.DATE_TIME_DATA,
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  listString: {
    fieldType: ECustomFieldType.LIST_STRING,
    minLength: 0,
    maxLength: 10,
    default: '',
    isScan: false,
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  eSign: {
    fieldType: ECustomFieldType.ESIGN,
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  files: {
    fieldType: ECustomFieldType.FILES,
    fileKind: EFileKind.UNKNOWN,
    overlayImage: '',
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  email: {
    fieldType: ECustomFieldType.EMAIL,
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  phoneNumber: {
    fieldType: ECustomFieldType.PHONE_NUMBER,
    countryCode: 'JP',
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  reference: {
    fieldType: ECustomFieldType.REFERENCE,
    minLength: 0,
    maxLength: 30,
    multiLine: false,
    template: '',
    default: '',
    customHeaders: {},
    customParams: {},
    ...OCR_FIELD_DEFAULT_DATA,
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  ghgEmission: {
    fieldType: ECustomFieldType.GHG_EMISSION,
    calculationType: '',
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  calculation: {
    fieldType: ECustomFieldType.CALCULATION,
    template: '',
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  weightMeasurement: {
    fieldType: ECustomFieldType.WEIGHT_MEASUREMENT,
    apiKey: '',
    deviceId: '',
    hasEncrypted: false,
    ...COMMON_CUSTOM_FIELD_DEFAULT_DATA,
  },
  adjustment: {
    ...numberInitObject,
    fieldType: ECustomFieldType.ADJUSTMENT,
    trackPointRoutes: [],
  },
})

export const POPULAR_COUNTRIES: string[] = ['JPN', 'USA', 'CHN']

export const DEFAULT_FIELD_RESTRICTION = Object.freeze({
  isMandatory: false,
  isAvailable: true,
} as IFieldRestriction)

export const DEFAULT_BARCODE_TYPES: Record<string, IBarcodeDefinitionType> = Object.freeze({
  default: ({
    name: '',
    foregroundColor: '#000000',
    backgroundColor: '#2196F3',
    activationFields: {
      origin: { ...DEFAULT_FIELD_RESTRICTION, copyOnRecycle: false },
      destination: { ...DEFAULT_FIELD_RESTRICTION, copyOnRecycle: false },
      product: { ...DEFAULT_FIELD_RESTRICTION, copyOnRecycle: false },
      customFields: {},
      trackingNumber: {
        ...DEFAULT_FIELD_RESTRICTION,
        label: '',
        default: '',
        copyOnRecycle: false,
        validationType: 'none',
        minLength: 0,
        maxLength: 10,
        isUnique: false,
        ocrImageAutoSaveField: '',
      },
      externalId: {
        ...DEFAULT_FIELD_RESTRICTION,
        label: '',
        default: '',
        copyOnRecycle: false,
        validationType: '',
        minLength: 0,
        maxLength: 10,
        isUnique: false,
        ocrImageAutoSaveField: '',
      },
      dimension: { ...DEFAULT_FIELD_RESTRICTION, copyOnRecycle: false },
    },
    barcodeRecycleAlertSetting: {
      recycleCountAlert: 0,
      emailDestinations: [''],
      emailDestinationAliases: [''],
      emailSubject: '',
      emailBody: '',
    },
    pairing: {},
    unpairing: {},
    linking: {},
    allowToBePaired: false,
    allowToPair: false,
    allowToBeLinked: false,
    barcodeLinkableTrackPoints: [] as string[],
    overridableStartTrackPoints: [] as string[],
    trackPointRouteRestrictedPaths: [] as IRestrictedTrackPointRoutePath[],
    bulkUpdatableTrackPoints: [] as string[],
    barcodeRecycleStartTrackPoint: '0',
    barcodeRecyclableTrackPoints: [] as string[],
    allowBarcodeRecycle: false,
    userTypes: [EUserType.CARRIER] as string[],
    countries: [] as string[],
    linkedBarcodeHrefType: ELinkedBarcodeHrefType.LATEST,
    uiConfig: {
      trackpointFlowchartVisibility: EDisplaySetting.VISIBLE,
      tracingVisibility: EDisplaySetting.VISIBLE,
      parentBarcodeVisibility: EDisplaySetting.VISIBLE,
      childrenBarcodeVisibility: EDisplaySetting.VISIBLE,
      listViewTitleLabelField: '',
      qrCodeVisibility: EDisplaySetting.VISIBLE,
      previewAddTrackingVisiblity: EDisplaySetting.HIDDEN,
    },
    clientVisibilities: [] as IClientVisibilities[],
    activationDataVisibility: {
      showOrigin: false,
      showDestination: false,
      showTrackingNumber: false,
      showExternalId: false,
      showDimension: false,
      showProduct: false,
      showCustomFields: [] as string[],
      showLinkedBarcodes: false,
    },
    externalWebAPIIntegration: {
      endpointUrl: '',
      authHeader: '',
      requestBody: {},
      buttonLabel: '',
    } as IExternalWebAPIIntegration,
    barcodeReportTemplates: [] as IBarcodeReportTemplate[],
    customValidators: [] as ICustomValidator[],
    CustomValidatorSuccesses: [] as ICustomValidatorSuccess[],
    isSkipCustomValidator: false,
    customNotificationEvent: [] as ICustomNotificationEvent[],
    trackPointTracingVisibilities: {},
    repeatedVisitingTrackPointAlertSettings: {} as Record<string, IRepeatedVisitingTrackPointNotificationDefinition>,
    trackPointRouteRemarks: [] as IRemarkRoute[],
    trackPointRouteNotifications: [] as INotificationRoute[],
    predefinedContact: {
      emails: [],
      numberContacts: [],
    },
    smapriTemplateConfig: {} as ISmaPriTemplateConfig,
  } as unknown) as IBarcodeDefinitionType,
})

export const BARCODE_SEARCH_KEYS: Record<string, string> = Object.freeze({
  orderNumber: 'orderNumber',
  manufactureCompletionDate: 'manufactureCompletionDate',
  createdAt: 'createdAt',
  manufacturedItemCode: 'manufacturedItemCode',
  manufacturedItemName: 'manufacturedItemName',
  componentItemCode: 'componentItemCode',
  componentItemName: 'componentItemName',
  // companyCode: 'companyCode',
  warehouseId: 'warehouseId',
  workCenterId: 'workCenterId',
  currentTrackPointKey: 'currentTrackPointKey',
  serialCode: 'serialCode',
  supplierId: 'supplierId',
})

export const BARCODE_TRACKING_SEARCH_KEYS: Record<string, string> = Object.freeze({
  customField: 'customFields',
  trackedDateTime: 'audit.trackedDateTime',
  trackedUser: 'audit.trackedUserId',
  isSkip: 'isSkip',
})

export const BARCODE_AUTO_COMPLETE_SEARCH_KEYS: string[] = [
  'activationData.externalId',
  'activationData.trackingNumber',
  'activationData.customFields.Depature-HotelName.text',
  'activationData.customFields.Arrival-HotelName.text',
]

export const BARCODE_GROUP_BY_USER_KEYS: Record<string, string> = Object.freeze({
  createdUser: 'audit.createdUserId',
  updatedUser: 'audit.updatedUserId',
})

export const BARCODE_GROUP_BY_DATE_KEYS: Record<string, string> = Object.freeze({
  activatedDateTime: 'audit.activatedDateTime',
  createdDate: 'audit.createdDateTime',
  updatedDate: 'audit.updatedDateTime',
  lastTrackingDateTime: 'audit.lastTrackingDateTime',
})

export const BARCODE_CURRENT_TP_KEYS: Record<string, string> = Object.freeze({
  currentTrackpoint: 'currentTrackPointKey',
})

export const BARCODE_GROUP_BY_KEYS: Record<string, string> = Object.freeze({
  ...BARCODE_GROUP_BY_USER_KEYS,
  ...BARCODE_GROUP_BY_DATE_KEYS,
  ...BARCODE_CURRENT_TP_KEYS,
  customField: 'activationData.customFields',
})

export const BARCODE_SEARCH_CUSTOM_FIELD_PREFIX = 'activationData.customFields'
export const BARCODE_SEARCH_TRACKING_DATA_CUSTOM_FIELD_PREFIX = 'trackingData.customFields'
export const BARCODE_IMPORT_CUSTOM_FIELD_KEY = 'customFields'

export const BARCODE_SEARCH_CUSTOM_FIELD_PATHS: Record<string, string> = Object.freeze({
  text: 'text',
  number: 'number',
  singleSelect: 'singleSelect.key',
  dateTimeData: 'dateTimeData',
  multiSelect: 'multiSelect.key',
  date: 'date',
  email: 'email',
  phoneNumber: 'phoneNumber.number',
  reference: 'reference.text',
  listString: 'listString',
})

export const BARCODE_SEARCH_VALUE_TYPES: Record<string, string> = Object.freeze({
  date: 'date',
  dateString: 'dateString',
  dateTimeData: 'dateTimeData',
  dateTimeString: 'dateTimeString',
  customField: 'customField',
  string: 'string',
  double: 'double',
  boolean: 'boolean',
  integer: 'integer',
})

export const BARCODE_SERCH_ADDRESS_KEYS: Record<string, string> = Object.freeze({
  country: 'country',
  postalCode: 'postalCode',
  prefecture: 'prefecture',
  address1: 'address1',
  address2: 'address2',
  phoneNumber: 'phoneNumber',
  companyName: 'companyName',
})

export const LABEL_CHARACTERS_MAX = 30
export const TABLE_SORT_DESC = 'descending'
export const TABLE_SORT_ASC = 'ascending'

export const TABLE_SORT_TYPE: Record<string, number> = Object.freeze({
  [TABLE_SORT_DESC]: -1,
  [TABLE_SORT_ASC]: 1,
})

export const BARCODE_IMPORT_LAYOUT_PDF_EXPORT: Record<string, number> = Object.freeze({
  a22: 0,
  a34: 1,
  a446: 2,
  a458: 3,
  a4710: 4,
})

export const BARCODE_IMPORT_ONLINE_TRAVEL_LAYOUT_PDF_EXPORT: Record<string, number> = Object.freeze({
  onlineTravel: 5,
})

export const MAX_CHARACTERS_SUPPORT_EXPORT_BARCODE: Record<string, number> = Object.freeze({
  a22: 25,
  a34: 14,
  a446: 18,
  a458: 14,
  a4710: 7,
})

export const BARCODE_IMPORT_NUMBER_ITEM_PDF_EXPORT: Record<number, number> = Object.freeze({
  0: 4,
  1: 12,
  2: 24,
  3: 40,
  4: 70,
  5: 4,
})

export const BARCODE_IMPORT_PDF_LABELS: Record<string, string> = Object.freeze({
  trackingNumber: 'trackingNumber',
  externalId: 'externalId',
})

export const BARCODE_IMPORT_PDF_MULTI_LINE_LAYOUT = [0, 1]

export const DEFAULT_CUSTOM_CONTENT_TEMPLATE = '<p>\n</p>'

export const COLOR_PALETTE = [
  '#7794DE',
  '#71C0D1',
  '#B2C734',
  '#E9C02F',
  '#E28F43',
  '#D06C9C',
  '#937DB7',
  '#768086',
  '#97AFEC',
  '#94D2DF',
  '#D3E277',
  '#EFE05B',
  '#EDBD74',
  '#DF8EB5',
  '#B9A8D4',
  '#B0B7BF',
  '#CADFF3',
  '#D1F2E6',
  '#E8F3A0',
  '#F9F2BA',
  '#F3DABD',
  '#F0C9DC',
  '#D5CEF1',
  '#DBDBDB',

  '#7794DE',
  '#71C0D1',
  '#B2C734',
  '#E9C02F',
  '#E28F43',
  '#D06C9C',
  '#937DB7',
  '#768086',
  '#97AFEC',
  '#94D2DF',
  '#D3E277',
  '#EFE05B',
  '#EDBD74',
  '#DF8EB5',
  '#B9A8D4',
  '#B0B7BF',
  '#CADFF3',
  '#D1F2E6',
  '#E8F3A0',
  '#F9F2BA',
  '#F3DABD',
  '#F0C9DC',
  '#D5CEF1',
  '#DBDBDB',

  '#7794DE',
  '#71C0D1',
  '#B2C734',
  '#E9C02F',
  '#E28F43',
  '#D06C9C',
  '#937DB7',
  '#768086',
  '#97AFEC',
  '#94D2DF',
  '#D3E277',
  '#EFE05B',
  '#EDBD74',
  '#DF8EB5',
  '#B9A8D4',
  '#B0B7BF',
  '#CADFF3',
  '#D1F2E6',
  '#E8F3A0',
  '#F9F2BA',
  '#F3DABD',
  '#F0C9DC',
  '#D5CEF1',
  '#DBDBDB',

  '#7794DE',
  '#71C0D1',
  '#B2C734',
  '#E9C02F',
  '#E28F43',
  '#D06C9C',
  '#937DB7',
  '#768086',
  '#97AFEC',
  '#94D2DF',
  '#D3E277',
  '#EFE05B',
  '#EDBD74',
  '#DF8EB5',
  '#B9A8D4',
  '#B0B7BF',
  '#CADFF3',
  '#D1F2E6',
  '#E8F3A0',
  '#F9F2BA',
  '#F3DABD',
  '#F0C9DC',
  '#D5CEF1',
  '#DBDBDB',
]

export const CUSTOM_FIELD_KEY: Record<string, string> = Object.freeze({
  text: 'text',
  number: 'number',
  singleSelect: 'singleSelect',
  multiSelect: 'multiSelect',
  date: 'date',
  dateTimeData: 'dateTimeData',
  eSign: 'eSign',
  files: 'files',
  email: 'email',
  phoneNumber: 'phoneNumber',
  reference: 'reference',
  adjustment: 'adjustment',
  listString: 'listString',
})

export const DEFAULT_PROJECT_TRACK_POINTS = Object.freeze({
  0: {
    name: 'Node 1',
    order: 1,
    isStart: true,
    isEnd: false,
  },
})

export const DEFAULT_PROJECT_INFORMATION = Object.freeze({
  mainInfo: {
    id: '',
    isDraft: false,
    draftId: '',
    name: '',
    code: '',
    allowUnmatchedTracking: false,
  },
  barcodeTypes: cloneDeep(DEFAULT_BARCODE_TYPES),
})

export const DEFAULT_PROJECT_INFORMATION_HAS_TRACKPOINT = Object.freeze({
  ...DEFAULT_PROJECT_INFORMATION,
  trackPoints: cloneDeep(DEFAULT_PROJECT_TRACK_POINTS),
})

export const DEFAULT_PROJECT_INFORMATION_NO_TRACKPOINT = Object.freeze({
  ...DEFAULT_PROJECT_INFORMATION,
  trackPoints: {},
})

export const CARRIER_USER_GROUP_DEFAULT = Object.freeze({
  name: '',
  enterpriseId: '',
  isDefault: false,
  projectPermissions: {} as ITrackingPermissions,
  userIds: [] as string[],
  isActive: true,
}) as ICarrierUserGroup

export const PROJECT_NEW_KEY_ID = 'projectId'

export const VALIDATION_EVENT_TYPE_VALUE: Record<string, string> = Object.freeze({
  tracked: 'tracked',
  paired: 'paired',
  linked: 'linked',
})

export const SUCCESS_VALIDATION_EVENT_TYPE_VALUE: Record<string, string> = Object.freeze({
  paired: 'paired',
  linked: 'linked',
})

export const COMPARE_METHOD_VALUE: Record<string, string> = Object.freeze({
  equal: 'equal',
  notEqual: 'notEqual',
})

export const VALIDATION_RESULT_TYPE_VALUE: Record<string, string> = Object.freeze({
  error: 'error',
})

export const LOGICAL_OPERATOR_VALUE: Record<string, string> = Object.freeze({
  and: 'and',
  or: 'or',
})

export const REPORT_TEMPLATE_FORMAT: Record<string, string> = Object.freeze({
  xlsx: 'xlsx',
  pdf: 'pdf',
})

export const BARCODE_EXPORT_FORMAT: Record<string, string> = Object.freeze({
  xlsx: 'xlsx',
  pdf: 'pdf',
  csv: 'csv',
  tsv: 'tsv',
  zip: 'zip',
  unknown: 'unknown',
})

export const COMPILE_LOGICAL_COMPARE_METHOD_VALUE: Record<string, string> = Object.freeze({
  andEqual: `${LOGICAL_OPERATOR_VALUE.and}:${COMPARE_METHOD_VALUE.equal}`,
  andNotEqual: `${LOGICAL_OPERATOR_VALUE.and}:${COMPARE_METHOD_VALUE.notEqual}`,
  orEqual: `${LOGICAL_OPERATOR_VALUE.or}:${COMPARE_METHOD_VALUE.equal}`,
  orNotEqual: `${LOGICAL_OPERATOR_VALUE.or}:${COMPARE_METHOD_VALUE.notEqual}`,
})

export const COMPILE_LOGICAL_CONDITION_METHOD_VALUE: Record<string, string> = Object.freeze({
  And: `${LOGICAL_OPERATOR_VALUE.and}`,
  Or: `${LOGICAL_OPERATOR_VALUE.or}`,
})

export const CUSTOM_VALIDATION_COMPARISON_DEFAULT = Object.freeze({
  from: '',
  to: '',
  operator: COMPARE_METHOD_VALUE.equal,
}) as ICustomValidatorComparison

export const CUSTOM_VALIDATION_CONDITION_DEFAULT = Object.freeze({
  comparisons: cloneDeep([CUSTOM_VALIDATION_COMPARISON_DEFAULT]),
  comparisonLogicalOperator: LOGICAL_OPERATOR_VALUE.and,
  compareMethod: COMPARE_METHOD_VALUE.equal,
}) as ICustomValidatorCondition

export const FILTER_DEBOUNCE_TIME = 400
export const TRACKING_POINT_NAME_DEBOUNCE_TIME = 400
export const EXPORT_BARCODES_LIMIT_NUMBER = 10_000

export const WEB_CLIP_ICON = Object.freeze(
  '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'
)

export const WEB_CLIP_ICON_NAME = Object.freeze('SmartBarcode')

export const POPULAR_COUNTRY_LIST = Object.freeze({
  JP: {
    code: 'JPN',
    mask: ['0##-####-####', '0##-###-####'],
    dialCode: '+81',
  },
  US: {
    code: 'USA',
    mask: ['1###-###-####'],
    dialCode: '+1',
  },
  CN: {
    code: 'CHN',
    mask: ['(###)####-####', '(###)###-####', '##-#####-#####'],
    dialCode: '+86',
  },
})

export const DRAFT_PROJECT_VERSION = -1

export const BARCODE_REPORT_TEMPLATE = Object.freeze({
  templateFilePath: '',
  buttonLabel: '',
  outputFileName: '{{id}}',
  fileFormatType: REPORT_TEMPLATE_FORMAT.xlsx,
  isDisableUploadFile: true,
  existFile: {} as UploadFile,
} as IBarcodeReportTemplate)

export const CLIENT_VISIBILITY_TEMPLATE = Object.freeze({
  activationDataVisibility: {
    showOrigin: false,
    showDestination: false,
    showTrackingNumber: false,
    showExternalId: false,
    showDimension: false,
    showProduct: false,
    showCustomFields: [] as string[],
    showLinkedBarcodes: false,
    hasActivationCustomFields: false,
  },
  trackPointTracingVisibilities: {},
  clientIds: [],
} as IClientVisibilities)

export const MASKED_ANNOTATION = '***'
export const MASKED_PACKAGE_DIMENSION = Object.freeze({
  width: MASKED_ANNOTATION,
  height: MASKED_ANNOTATION,
  depth: MASKED_ANNOTATION,
  weight: MASKED_ANNOTATION,
})

export const MASKED_ADDRESS: IAddress = Object.freeze({
  companyName: MASKED_ANNOTATION,
  country: MASKED_ANNOTATION,
  postalCode: MASKED_ANNOTATION,
  prefecture: MASKED_ANNOTATION,
  address1: MASKED_ANNOTATION,
  address2: MASKED_ANNOTATION,
  phoneNumber: MASKED_ANNOTATION,
})

export const SB_FORCE_UPGRADE_VERSION_KEY = 'x-smartbarcode-is-forced-version'
export const SB_MOBILE_VERSION_KEY = 'x-smartbarcode-mobile-version'
export const SB_PORTAL_VERSION_KEY = 'x-smartbarcode-portal-version'

export const IMAGES_EXTENSIONS = Object.freeze(['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.webp', '.svg'])

export const OTHER_COUNTRY_LIST = Object.freeze(({
  JP: {
    code: 'JPN',
    mask: ['###-####-####', '##-####-####'],
    dialCode: '+81',
  },
  US: {
    code: 'USA',
    mask: ['###-####-####', '##-####-####'],
    dialCode: '+1',
  },
  CN: {
    code: 'CHN',
    mask: ['###-####-####', '##-####-####'],
    dialCode: '+86',
  },
  AD: {
    code: 'AND',
    mask: ['###-###'],
    dialCode: '+376',
  },
  AE: {
    code: 'ARE',
    mask: ['0##-###-####', '0#-###-####'],
    dialCode: '+971',
  },
  AF: {
    code: 'AFG',
    mask: ['0##-###-####'],
    dialCode: '+93',
  },
  AG: {
    code: 'ATG',
    mask: ['1###-####'],
    dialCode: '+1268',
  },
  AI: {
    code: 'AIA',
    mask: ['1###-####'],
    dialCode: '+1264',
  },
  AL: {
    code: 'ALB',
    mask: ['0###-###-###'],
    dialCode: '+355',
  },
  AM: {
    code: 'ARM',
    mask: ['0##-###-###'],
    dialCode: '+374',
  },
  AO: {
    code: 'AGO',
    mask: ['###-###-###'],
    dialCode: '+244',
  },
  AQ: {
    code: 'ATA',
    mask: ['###-###'],
    dialCode: '+672',
  },
  AR: {
    code: 'ARG',
    mask: ['0###-###-####'],
    dialCode: '+54',
  },
  AS: {
    code: 'ASM',
    mask: ['1###-####'],
    dialCode: '+1684',
  },
  AT: {
    code: 'AUT',
    mask: ['0###-###-####'],
    dialCode: '+43',
  },
  AU: {
    code: 'AUS',
    mask: ['0#-####-####'],
    dialCode: '+61',
  },
  AX: {
    code: 'ALA',
    mask: ['0 457 ###-####'],
    dialCode: '+358',
  },
  AW: {
    code: 'ABW',
    mask: ['###-####'],
    dialCode: '+297',
  },
  AZ: {
    code: 'AZE',
    mask: ['0##-###-##-##'],
    dialCode: '+994',
  },
  BA: {
    code: 'BIH',
    mask: ['0##-#####', '0##-####'],
    dialCode: '+387',
  },
  BB: {
    code: 'BRB',
    mask: ['1###-####'],
    dialCode: '+1246',
  },
  BD: {
    code: 'BGD',
    mask: ['0##-###-###'],
    dialCode: '+880',
  },
  BE: {
    code: 'BEL',
    mask: ['0###-###-###'],
    dialCode: '+32',
  },
  BF: {
    code: 'BFA',
    mask: ['##-##-####'],
    dialCode: '+226',
  },
  BG: {
    code: 'BGR',
    mask: ['0###-###-###'],
    dialCode: '+359',
  },
  BH: {
    code: 'BHR',
    mask: ['####-####'],
    dialCode: '+973',
  },
  BI: {
    code: 'BDI',
    mask: ['##-##-####'],
    dialCode: '+257',
  },
  BJ: {
    code: 'BEN',
    mask: ['##-##-####'],
    dialCode: '+229',
  },
  BQ: {
    code: 'BES',
    mask: null,
    dialCode: '',
  },
  BL: {
    code: 'BLM',
    mask: null,
    dialCode: '+590',
  },
  BM: {
    code: 'BMU',
    mask: ['1###-####'],
    dialCode: '+1441',
  },
  BN: {
    code: 'BRN',
    mask: ['###-####'],
    dialCode: '+673',
  },
  BO: {
    code: 'BOL',
    mask: ['0#-###-####'],
    dialCode: '+591',
  },
  BR: {
    code: 'BRA',
    mask: ['0##-####-####'],
    dialCode: '+55',
  },
  BS: {
    code: 'BHS',
    mask: ['###-####'],
    dialCode: '+1242',
  },
  BT: {
    code: 'BTN',
    mask: ['##-######', '#-######'],
    dialCode: '+975',
  },
  BV: {
    code: 'BVT',
    mask: null,
    dialCode: '',
  },
  BW: {
    code: 'BWA',
    mask: ['###-#####', '##-#####'],
    dialCode: '+267',
  },
  BY: {
    code: 'BLR',
    mask: ['80#-###-####.'],
    dialCode: '+375',
  },
  BZ: {
    code: 'BLZ',
    mask: ['###-####'],
    dialCode: '+501',
  },
  CA: {
    code: 'CAN',
    mask: ['1(###)###-####'],
    dialCode: '+1',
  },
  CC: {
    code: 'CCK',
    mask: null,
    dialCode: '+61',
  },
  CD: {
    code: 'COD',
    mask: ['0(###)###-###'],
    dialCode: '+243',
  },
  CF: {
    code: 'CAF',
    mask: ['##-##-####'],
    dialCode: '+236',
  },
  CG: {
    code: 'COG',
    mask: ['##-###-####'],
    dialCode: '+242',
  },
  CH: {
    code: 'CHE',
    mask: ['0##-###-####'],
    dialCode: '+41',
  },
  CI: {
    code: 'CIV',
    mask: ['##-####-####'],
    dialCode: '+225',
  },
  CK: {
    code: 'COK',
    mask: ['##-###'],
    dialCode: '+682',
  },
  CL: {
    code: 'CHL',
    mask: ['#-####-####'],
    dialCode: '+56',
  },
  CM: {
    code: 'CMR',
    mask: ['####-####'],
    dialCode: '+237',
  },
  CO: {
    code: 'COL',
    mask: ['0(###)###-####'],
    dialCode: '+57',
  },
  CR: {
    code: 'CRI',
    mask: ['####-####'],
    dialCode: '+506',
  },
  CU: {
    code: 'CUB',
    mask: ['0#-###-####'],
    dialCode: '+53',
  },
  CV: {
    code: 'CPV',
    mask: ['(###)##-##'],
    dialCode: '+238',
  },
  CW: {
    code: 'CUW',
    mask: ['0###-####'],
    dialCode: '+599',
  },
  CY: {
    code: 'CYP',
    mask: ['##-###-###'],
    dialCode: '+357',
  },
  CX: {
    code: 'CXR',
    mask: null,
    dialCode: '+61',
  },
  CZ: {
    code: 'CZE',
    mask: ['(###)###-###'],
    dialCode: '+420',
  },
  DE: {
    code: 'DEU',
    mask: ['0(####)###-####', '0(###)###-####', '0(###)##-####', '0(###)##-###', '0(###)##-##', '0###-###'],
    dialCode: '+49',
  },
  DJ: {
    code: 'DJI',
    mask: ['##-##-##-##'],
    dialCode: '+253',
  },
  DK: {
    code: 'DNK',
    mask: ['##-##-##-##'],
    dialCode: '+45',
  },
  DM: {
    code: 'DMA',
    mask: ['1###-####'],
    dialCode: '+1767',
  },
  DO: {
    code: 'DOM',
    mask: ['1###-####'],
    dialCode: '+1849',
  },
  DZ: {
    code: 'DZA',
    mask: ['0##-###-####'],
    dialCode: '+213',
  },
  EC: {
    code: 'ECU',
    mask: ['0##-###-####', '0#-###-####'],
    dialCode: '+593',
  },
  EE: {
    code: 'EST',
    mask: ['####-####', '###-####'],
    dialCode: '+372',
  },
  EG: {
    code: 'EGY',
    mask: ['0(###)###-####'],
    dialCode: '+20',
  },
  EH: {
    code: 'ESH',
    mask: null,
    dialCode: '+212',
  },
  ER: {
    code: 'ERI',
    mask: ['0#-###-###'],
    dialCode: '+291',
  },
  ES: {
    code: 'ESP',
    mask: ['(###)###-###'],
    dialCode: '+34',
  },
  ET: {
    code: 'ETH',
    mask: ['0##-###-####'],
    dialCode: '+251',
  },
  FI: {
    code: 'FIN',
    mask: ['0(###)###-##-##'],
    dialCode: '+358',
  },
  FJ: {
    code: 'FJI',
    mask: ['##-#####'],
    dialCode: '+679',
  },
  FK: {
    code: 'FLK',
    mask: ['#####'],
    dialCode: '+500',
  },
  FM: {
    code: 'FSM',
    mask: ['1###-####'],
    dialCode: '+691',
  },
  FO: {
    code: 'FRO',
    mask: ['###-###'],
    dialCode: '+298',
  },
  FR: {
    code: 'FRA',
    mask: ['0(###)###-###'],
    dialCode: '+33',
  },
  GA: {
    code: 'GAB',
    mask: ['0#-##-##-##'],
    dialCode: '+241',
  },
  GD: {
    code: 'GRD',
    mask: ['1###-####'],
    dialCode: '+1473',
  },
  GE: {
    code: 'GEO',
    mask: ['0(###)###-###'],
    dialCode: '+995',
  },
  GF: {
    code: 'GUF',
    mask: ['0#####-####'],
    dialCode: '+594',
  },
  GG: {
    code: 'GGY',
    mask: null,
    dialCode: '+44',
  },
  GH: {
    code: 'GHA',
    mask: ['0(###)###-###'],
    dialCode: '+233',
  },
  GI: {
    code: 'GIB',
    mask: ['###-#####'],
    dialCode: '+350',
  },
  GL: {
    code: 'GRL',
    mask: ['##-##-##'],
    dialCode: '+299',
  },
  GM: {
    code: 'GMB',
    mask: ['(###)##-##'],
    dialCode: '+220',
  },
  GN: {
    code: 'GIN',
    mask: ['###-###-###'],
    dialCode: '+224',
  },
  GP: {
    code: 'GLP',
    mask: null,
    dialCode: '+590',
  },
  GQ: {
    code: 'GNQ',
    mask: ['##-###-####'],
    dialCode: '+240',
  },
  GR: {
    code: 'GRC',
    mask: ['(###)###-####'],
    dialCode: '+30',
  },
  GS: {
    code: 'SGS',
    mask: null,
    dialCode: '+500',
  },
  GT: {
    code: 'GTM',
    mask: ['#-###-####'],
    dialCode: '+502',
  },
  GU: {
    code: 'GUM',
    mask: ['1###-####'],
    dialCode: '+1671',
  },
  GW: {
    code: 'GNB',
    mask: ['#-######'],
    dialCode: '+245',
  },
  GY: {
    code: 'GUY',
    mask: ['###-####'],
    dialCode: '+592',
  },
  HK: {
    code: 'HKG',
    mask: ['####-####'],
    dialCode: '+852',
  },
  HM: {
    code: 'HMD',
    mask: null,
    dialCode: '',
  },
  HN: {
    code: 'HND',
    mask: ['####-####'],
    dialCode: '+504',
  },
  HR: {
    code: 'HRV',
    mask: ['0##-###-###'],
    dialCode: '+385',
  },
  HT: {
    code: 'HTI',
    mask: ['##-##-####'],
    dialCode: '+509',
  },
  HU: {
    code: 'HUN',
    mask: ['06(###)###-###'],
    dialCode: '+36',
  },
  ID: {
    code: 'IDN',
    mask: ['0(8##)###-####', '0##-###-##', '0##-###-###', '0##-###-####', '8##-####-####'],
    dialCode: '+62',
  },
  IE: {
    code: 'IRL',
    mask: ['0(###)###-###'],
    dialCode: '+353',
  },
  IL: {
    code: 'ISR',
    mask: ['05#-###-####', '0#-###-####'],
    dialCode: '+972',
  },
  IM: {
    code: 'IMN',
    mask: null,
    dialCode: '+44',
  },
  IN: {
    code: 'IND',
    mask: ['0(####)###-###'],
    dialCode: '+91',
  },
  IO: {
    code: 'IOT',
    mask: ['###-####'],
    dialCode: '+246',
  },
  IQ: {
    code: 'IRQ',
    mask: ['(###)###-####', '(##)###-###', '(##)###-####'],
    dialCode: '+964',
  },
  IR: {
    code: 'IRN',
    mask: ['0(###)###-####'],
    dialCode: '+98',
  },
  IS: {
    code: 'ISL',
    mask: ['###-####'],
    dialCode: '+354',
  },
  IT: {
    code: 'ITA',
    mask: ['(###)####-###'],
    dialCode: '+39',
  },
  JE: {
    code: 'JEY',
    mask: null,
    dialCode: '+44',
  },
  JM: {
    code: 'JAM',
    mask: ['1###-####'],
    dialCode: '+1876',
  },
  JO: {
    code: 'JOR',
    mask: ['0#-####-####'],
    dialCode: '+962',
  },
  KE: {
    code: 'KEN',
    mask: ['0###-######'],
    dialCode: '+254',
  },
  KG: {
    code: 'KGZ',
    mask: ['0(###)###-###'],
    dialCode: '+996',
  },
  KH: {
    code: 'KHM',
    mask: ['0##-###-###'],
    dialCode: '+855',
  },
  KI: {
    code: 'KIR',
    mask: ['###-####'],
    dialCode: '+686',
  },
  KM: {
    code: 'COM',
    mask: ['##-#####'],
    dialCode: '+269',
  },
  KN: {
    code: 'KNA',
    mask: ['1###-####'],
    dialCode: '+1869',
  },
  KP: {
    code: 'PRK',
    mask: ['###-####-###', '####-####'],
    dialCode: '+850',
  },
  KR: {
    code: 'KOR',
    mask: ['0##-###-####', '0##-####-####'],
    dialCode: '+82',
  },
  KW: {
    code: 'KWT',
    mask: ['####-####'],
    dialCode: '+965',
  },
  KY: {
    code: 'CYM',
    mask: ['1###-####'],
    dialCode: '+345',
  },
  KZ: {
    code: 'KAZ',
    mask: ['8(6##)###-##-##', '0(7##)###-##-##'],
    dialCode: '+77',
  },
  LA: {
    code: 'LAO',
    mask: ['0(20##)###-###', '0##-###-###'],
    dialCode: '+856',
  },
  LB: {
    code: 'LBN',
    mask: ['0##-###-###', '0#-###-###'],
    dialCode: '+961',
  },
  LC: {
    code: 'LCA',
    mask: ['1###-####'],
    dialCode: '+1758',
  },
  LI: {
    code: 'LIE',
    mask: ['###-####'],
    dialCode: '+423',
  },
  LK: {
    code: 'LKA',
    mask: ['0##-###-####'],
    dialCode: '+94',
  },
  LR: {
    code: 'LBR',
    mask: ['##-###-####'],
    dialCode: '+231',
  },
  LS: {
    code: 'LSO',
    mask: ['#-###-####'],
    dialCode: '+266',
  },
  LT: {
    code: 'LTU',
    mask: ['8(###)##-###'],
    dialCode: '+370',
  },
  LU: {
    code: 'LUX',
    mask: ['(###)###-###', '(###)##-###', '(###)####', '(###)###', '(###)##'],
    dialCode: '+352',
  },
  LV: {
    code: 'LVA',
    mask: ['##-###-###'],
    dialCode: '+371',
  },
  LY: {
    code: 'LBY',
    mask: ['0##-###-###'],
    dialCode: '+218',
  },
  MA: {
    code: 'MAR',
    mask: ['0##-####-###'],
    dialCode: '+212',
  },
  MC: {
    code: 'MCO',
    mask: ['(###)###-###', '##-###-###'],
    dialCode: '+377',
  },
  MD: {
    code: 'MDA',
    mask: ['0####-####'],
    dialCode: '+373',
  },
  ME: {
    code: 'MNE',
    mask: ['0##-###-###'],
    dialCode: '+382',
  },
  MG: {
    code: 'MDG',
    mask: ['0##-##-#####'],
    dialCode: '+261',
  },
  MH: {
    code: 'MHL',
    mask: ['1###-####'],
    dialCode: '+692',
  },
  MK: {
    code: 'MKD',
    mask: ['0##-###-###'],
    dialCode: '+389',
  },
  ML: {
    code: 'MLI',
    mask: ['##-##-####'],
    dialCode: '+223',
  },
  MM: {
    code: 'MMR',
    mask: ['0##-###-###', '0#-###-###', '0-###-###'],
    dialCode: '+95',
  },
  MN: {
    code: 'MNG',
    mask: ['0##-##-####'],
    dialCode: '+976',
  },
  MO: {
    code: 'MAC',
    mask: ['####-####'],
    dialCode: '+853',
  },
  MP: {
    code: 'MNP',
    mask: ['1###-####'],
    dialCode: '+1670',
  },
  MQ: {
    code: 'MTQ',
    mask: ['0(###)##-##-##'],
    dialCode: '+596',
  },
  MR: {
    code: 'MRT',
    mask: ['##-##-####'],
    dialCode: '+222',
  },
  MS: {
    code: 'MSR',
    mask: ['1###-####'],
    dialCode: '+1664',
  },
  MT: {
    code: 'MLT',
    mask: ['####-####'],
    dialCode: '+356',
  },
  MU: {
    code: 'MUS',
    mask: ['###-####', '####-####'],
    dialCode: '+230',
  },
  MV: {
    code: 'MDV',
    mask: ['###-####'],
    dialCode: '+960',
  },
  MW: {
    code: 'MWI',
    mask: ['#-###-###', '####-####', '#-####-####'],
    dialCode: '+265',
  },
  MX: {
    code: 'MEX',
    mask: ['(###)###-####'],
    dialCode: '+52',
  },
  MY: {
    code: 'MYS',
    mask: ['0##-###-####', '0#-###-###'],
    dialCode: '+60',
  },
  MZ: {
    code: 'MOZ',
    mask: ['##-###-###', '###-###-###'],
    dialCode: '+258',
  },
  NA: {
    code: 'NAM',
    mask: ['0##-###-####'],
    dialCode: '+264',
  },
  NC: {
    code: 'NCL',
    mask: ['###-###'],
    dialCode: '+687',
  },
  NE: {
    code: 'NER',
    mask: ['##-##-####'],
    dialCode: '+227',
  },
  NF: {
    code: 'NFK',
    mask: ['03##-###', '35#-###', '32#-###'],
    dialCode: '+672',
  },
  NG: {
    code: 'NGA',
    mask: ['0(###)###-####'],
    dialCode: '+234',
  },
  NI: {
    code: 'NIC',
    mask: ['####-####'],
    dialCode: '+505',
  },
  NL: {
    code: 'NLD',
    mask: ['0##-###-####'],
    dialCode: '+31',
  },
  NO: {
    code: 'NOR',
    mask: ['###-###-####', '####-###-###'],
    dialCode: '+47',
  },
  NP: {
    code: 'NPL',
    mask: ['0##-###-###'],
    dialCode: '+977',
  },
  NR: {
    code: 'NRU',
    mask: ['###-####'],
    dialCode: '+674',
  },
  NU: {
    code: 'NIU',
    mask: ['####'],
    dialCode: '+683',
  },
  NZ: {
    code: 'NZL',
    mask: ['0(###)###-###', '0##-###-###', '0(###)###-####'],
    dialCode: '+64',
  },
  OM: {
    code: 'OMN',
    mask: ['##-###-###'],
    dialCode: '+968',
  },
  PA: {
    code: 'PAN',
    mask: ['###-####', '####-####'],
    dialCode: '+507',
  },
  PE: {
    code: 'PER',
    mask: ['0(###)###-###'],
    dialCode: '+51',
  },
  PF: {
    code: 'PYF',
    mask: ['##-##-##-##'],
    dialCode: '+689',
  },
  PG: {
    code: 'PNG',
    mask: ['(###)##-###', '###-####'],
    dialCode: '+675',
  },
  PH: {
    code: 'PHL',
    mask: ['0(###)###-####'],
    dialCode: '+63',
  },
  PK: {
    code: 'PAK',
    mask: ['0(###)###-####'],
    dialCode: '+92',
  },
  PL: {
    code: 'POL',
    mask: ['(###)###-###'],
    dialCode: '+48',
  },
  PM: {
    code: 'SPM',
    mask: ['###-###'],
    dialCode: '+508',
  },
  PN: {
    code: 'PCN',
    mask: null,
    dialCode: '+872',
  },
  PR: {
    code: 'PRI',
    mask: null,
    dialCode: '+1939',
  },
  PS: {
    code: 'PSE',
    mask: ['0##-###-####'],
    dialCode: '+970',
  },
  PT: {
    code: 'PRT',
    mask: ['##-###-####'],
    dialCode: '+351',
  },
  PW: {
    code: 'PLW',
    mask: ['###-####'],
    dialCode: '+680',
  },
  PY: {
    code: 'PRY',
    mask: ['0(###)###-###'],
    dialCode: '+595',
  },
  QA: {
    code: 'QAT',
    mask: ['####-####'],
    dialCode: '+974',
  },
  RE: {
    code: 'REU',
    mask: ['0#####-####'],
    dialCode: '+262',
  },
  RO: {
    code: 'ROU',
    mask: ['0##-###-####'],
    dialCode: '+40',
  },
  RS: {
    code: 'SRB',
    mask: ['0##-###-####'],
    dialCode: '+381',
  },
  RU: {
    code: 'RUS',
    mask: ['8(###)###-##-##'],
    dialCode: '+7',
  },
  RW: {
    code: 'RWA',
    mask: ['(###)###-###'],
    dialCode: '+250',
  },
  SA: {
    code: 'SAU',
    mask: ['05-####-####', '0#-###-####'],
    dialCode: '+966',
  },
  SB: {
    code: 'SLB',
    mask: ['###-####', '#####'],
    dialCode: '+677',
  },
  SC: {
    code: 'SYC',
    mask: ['#-###-###'],
    dialCode: '+248',
  },
  SD: {
    code: 'SDN',
    mask: ['0##-###-####'],
    dialCode: '+249',
  },
  SE: {
    code: 'SWE',
    mask: ['0##-###-####'],
    dialCode: '+46',
  },
  SG: {
    code: 'SGP',
    mask: ['####-####'],
    dialCode: '+65',
  },
  SH: {
    code: 'SHN',
    mask: ['#####'],
    dialCode: '+290',
  },
  SI: {
    code: 'SVN',
    mask: ['0##-###-###'],
    dialCode: '+386',
  },
  SJ: {
    code: 'SJM',
    mask: null,
    dialCode: '+47',
  },
  SK: {
    code: 'ARE',
    mask: ['0###-###-###'],
    dialCode: '+421',
  },
  SL: {
    code: 'SLE',
    mask: ['0##-######'],
    dialCode: '+232',
  },
  SM: {
    code: 'SMR',
    mask: ['####-######', '####-####'],
    dialCode: '+378',
  },
  SN: {
    code: 'SEN',
    mask: ['##-###-####'],
    dialCode: '+221',
  },
  SO: {
    code: 'SOM',
    mask: ['###-###-###', '##-###-###', '#-###-###', '###-###'],
    dialCode: '+252',
  },
  SR: {
    code: 'SUR',
    mask: ['0###-####', '0###-###'],
    dialCode: '+597',
  },
  SS: {
    code: 'SSD',
    mask: ['##-###-####'],
    dialCode: '+211',
  },
  ST: {
    code: 'STP',
    mask: ['##-#####'],
    dialCode: '+239',
  },
  SV: {
    code: 'SLV',
    mask: ['##-##-####'],
    dialCode: '+503',
  },
  SX: {
    code: 'SXM',
    mask: ['1###-####'],
    dialCode: '+721',
  },
  SY: {
    code: 'SYR',
    mask: ['0##-####-###'],
    dialCode: '+963',
  },
  SZ: {
    code: 'SWZ',
    mask: ['##-##-####'],
    dialCode: '+268',
  },
  TC: {
    code: 'TCA',
    mask: ['1###-####'],
    dialCode: '+1649',
  },
  TD: {
    code: 'TCD',
    mask: ['##-##-##-##'],
    dialCode: '+235',
  },
  TF: {
    code: 'ATF',
    mask: null,
    dialCode: '',
  },
  TG: {
    code: 'TGO',
    mask: ['##-###-###'],
    dialCode: '+228',
  },
  TH: {
    code: 'THA',
    mask: ['0##-###-####'],
    dialCode: '+66',
  },
  TJ: {
    code: 'TJK',
    mask: ['8##-###-####'],
    dialCode: '+992',
  },
  TK: {
    code: 'TKL',
    mask: ['####', '###-####'],
    dialCode: '+690',
  },
  TL: {
    code: 'TLS',
    mask: ['###-####', '###-#####'],
    dialCode: '+670',
  },
  TM: {
    code: 'TKM',
    mask: ['8#-###-####'],
    dialCode: '+993',
  },
  TN: {
    code: 'TUN',
    mask: ['##-###-###'],
    dialCode: '+216',
  },
  TO: {
    code: 'TON',
    mask: ['#####', '##-#####'],
    dialCode: '+676',
  },
  TR: {
    code: 'TUR',
    mask: ['0###-###-####'],
    dialCode: '+90',
  },
  TT: {
    code: 'TTO',
    mask: ['1###-####'],
    dialCode: '+1868',
  },
  TV: {
    code: 'TUV',
    mask: ['#####', '##-####', '###-####'],
    dialCode: '+688',
  },
  TW: {
    code: 'TWN',
    mask: ['0#-####-####', '0####-####'],
    dialCode: '+886',
  },
  TZ: {
    code: 'TZA',
    mask: ['0##-###-####'],
    dialCode: '+255',
  },
  UA: {
    code: 'UKR',
    mask: ['0##-###-##-##'],
    dialCode: '+380',
  },
  UG: {
    code: 'UGA',
    mask: ['0###-###-###'],
    dialCode: '+256',
  },
  UK: {
    code: 'GBR',
    mask: ['0##-####-####'],
    dialCode: '+44',
  },
  UY: {
    code: 'URY',
    mask: ['0#-###-##-##'],
    dialCode: '+598',
  },
  UZ: {
    code: 'UZB',
    mask: ['0##-###-####'],
    dialCode: '+998',
  },
  VA: {
    code: 'VAT',
    mask: ['06-698-#####'],
    dialCode: '+379',
  },
  VC: {
    code: 'VCT',
    mask: ['1###-####'],
    dialCode: '+1784',
  },
  VE: {
    code: 'VEN',
    mask: ['0###-###-####'],
    dialCode: '+58',
  },
  VG: {
    code: 'VGB',
    mask: ['1###-####'],
    dialCode: '+1284',
  },
  VI: {
    code: 'VIR',
    mask: ['1###-####'],
    dialCode: '+1340',
  },
  VN: {
    code: 'VNM',
    mask: ['0##-####-###'],
    dialCode: '+84',
  },
  VU: {
    code: 'VUT',
    mask: ['##-#####', '#####'],
    dialCode: '+678',
  },
  WF: {
    code: 'WLF',
    mask: ['##-####'],
    dialCode: '+681',
  },
  WS: {
    code: 'WSM',
    mask: ['#-####', '###-####'],
    dialCode: '+685',
  },
  YE: {
    code: 'YEM',
    mask: ['0###-###-###', '0#-###-###', '0##-###-###'],
    dialCode: '+967',
  },
  YT: {
    code: 'MYT',
    mask: ['0-##-##-##'],
    dialCode: '+262',
  },
  ZA: {
    code: 'ZAF',
    mask: ['0##-###-####'],
    dialCode: '+27',
  },
  ZM: {
    code: 'ZMB',
    mask: ['0##-###-####'],
    dialCode: '+260',
  },
  ZW: {
    code: 'ZWE',
    mask: ['0#-######'],
    dialCode: '+263',
  },
} as unknown) as Record<string, ICountryPhoneInfo>)

export const ZIPCODE_MASK: Record<string, string> = Object.freeze({
  JPN: '###-####',
  USA: '#####-####',
  CHN: '######',
})

export const PHONE_MASK: Record<string, string> = Object.freeze({
  JPN: '###-####-####',
  USA: '###-###-####',
  CHN: '###########',
  DEFAULT: '###############',
})

export const FIELD_TYPE: Record<string, string> = Object.freeze({
  TEXT: 'text',
  NUMBER: 'number',
  ADJUSTMENT: 'adjustment',
  SINGLE_SELECT: 'singleSelect',
  MULTI_SELECT: 'multiSelect',
  DATE: 'date',
  DATE_TIME: 'dateTimeData',
  ESIGN: 'eSign',
  FILES: 'files',
  LOCATION: 'location',
  PHONE_NUMBER: 'phoneNumber',
  EMAIL: 'email',
  REFERENCE: 'reference',
  GHG_EMISSION: 'ghgEmission',
  CALCULATION: 'calculation',
  WEIGHT_MEASUREMENT: 'weightMeasurement',
  LIST_STRING: 'listString',
})

export const BARCODE_ICONS: Record<string, string> = Object.freeze({
  ORPHAN: 'orphan',
  NODE: 'node',
  LEAF: 'leaf',
  VACCINE: 'vaccine',
  TRUCK: 'truck',
  CONTAINER: 'container',
  TIMETABLE: 'timetable',
  INVENTORY: 'inventory',
  WAREHOUSE: 'warehouse',
  RACK: 'rack',
  FORKLIFT: 'forklift',
  PACKAGES: 'packages',
  PACKAGE: 'package',
  PAPER: 'paper',
  PERSON: 'person',
  TRAIN: 'train',
  SHIP: 'ship',
  AIRPLANE: 'airplane',
})

export const DATE_TIME: Record<string, string> = Object.freeze({
  PARAM_FORMAT: 'YYYYMMDD',
  DISPLAY_FORMAT: 'YYYY/MM/DD',
  RESPONSE_FORMAT: 'YYYY-MM-DD',
  FORMAT: 'YYYY/MM/DD h:mm a',
  WITH_SECOND: 'yyyy-MM-dd kk:mm:ss',
  TIME_WITHOUT_SECOND_FORMAT: 'HH:mm',
  WITHOUT_SECOND_FORMAT: 'YYYY/MM/DD HH:mm',
  LL_DATE_DISPLAY_FORMAT: 'LL',
  CSV_FORMAT: 'yyyyMMdd_HHmmss',
})

export const LINKED_BARCODE_HREF_TYPE = Object.freeze({
  snapshot: 'snapshot',
  latest: 'latest',
})

export const LINKED_BARCODE_PATH_KEY = Object.freeze({
  activationData: 'activationData',
  trackingData: 'trackingData',
  linkedBarcodes: 'linkedBarcodes',
})

export const BARCODE_ACTIVATION_DATA_KEYS: Record<string, string> = Object.freeze({
  customTextBoxes: 'text',
  customTextAreas: 'textarea',
  customNumberInputs: 'number',
  customRadioButtons: 'radio',
  customCheckBoxes: 'check',
  customDateInputs: 'date',
})

export const MASKED_STR = '***'

export const MARUMIYA_PACK = 'marumiyaPack'
